<template>
  <v-form @submit.prevent="submitSearch">
    <div v-for="(row, index) in rows" :key="index" class="mb-4">
      <v-row align="center">
        <!-- Operator ab 2. Zeile -->
        <v-col cols="12" md="2" v-if="index !== 0">
          <v-select
              :items="operatorItems"
              item-value="value"
              item-title="text"
              v-model="row.selected_operator"
              label="Operator"
              dense
              hide-details="auto"
          />
        </v-col>

        <!-- Feld -->
        <v-col cols="12" md="3">
          <v-select
              :items="fieldItems"
              item-value="value"
              item-title="text"
              v-model="row.selected_field"
              label="Field"
              dense
              hide-details="auto"
          />
        </v-col>

        <!-- Suchterm -->
        <v-col cols="12" md="6">
          <v-text-field
              v-model="row.submitted_input"
              label="Search term"
              placeholder="e.g. Mose"
              dense
              hide-details="auto"
          />
        </v-col>

        <!-- Delete ab 2. Zeile -->
        <v-col cols="12" md="1" v-if="index !== 0">
          <v-btn icon color="error" @click="removeRow(index)" size="small">
            <v-icon>fas fa-trash</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Footer Buttons wie Graph -->
    <v-row class="mt-4" align="center">
      <v-col cols="12" md="6">
        <v-btn color="primary" @click="addRow" size="small">
          <v-icon>fas fa-plus</v-icon>
          Add field
        </v-btn>
      </v-col>

      <v-col cols="12" md="6" class="text-md-right text-end">
        <v-btn color="success" type="submit">
          <v-icon>fas fa-search</v-icon>
          Search
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
export default {
  name: "CmAdvancedSearchApp",
  props: {
    searchUrl: {type: String, required: true},
  },
  data() {
    return {
      rows: [],
      operatorItems: [
        {text: "AND", value: " AND "},
        {text: "OR", value: " OR "},
        {text: "NOT", value: " NOT "},
      ],

      // ✅ CM_Meta Solr-Felder (bitte bei Bedarf ergänzen)
      fieldItems: [
        {text: "Full text", value: "text:"},
        {text: "Journal title", value: "j_title:"},
        {text: "Date issued", value: "dateIssued:"},
        {text: "Volume", value: "volume:"},
        {text: "Issue", value: "heft:"},
        {text: "Essay", value: "aufsatz:"},
        {text: "Page", value: "page:"},
      ],
    };
  },

  mounted() {
    // 1) q aus URL parsen (wie Graph)
    const q = new URLSearchParams(window.location.search).get("q");
    if (q) {
      const parsed = this.parseQueryToRows(q);
      if (parsed.length > 0) {
        this.rows = parsed;
        return;
      }
    }

    // 2) Default
    this.initializeDefaultRows();
  },

  methods: {
    initializeDefaultRows() {
      this.rows = [
        {selected_operator: "", selected_field: "text:", submitted_input: ""},
        {selected_operator: " AND ", selected_field: "j_title:", submitted_input: ""},
      ];
    },

    addRow() {
      this.rows.push({
        selected_operator: " AND ",
        selected_field: "text:",
        submitted_input: "",
      });
    },

    removeRow(index) {
      if (index > 0) this.rows.splice(index, 1);
    },

    // ---- Solr helpers ----
    escapeSolrValue(val) {
      // Minimal robust escaping für Solr/Lucene special chars
      // + - && || ! ( ) { } [ ] ^ " ~ * ? : \ /
      return val.replace(/([+\-!(){}\[\]^"~*?:\\/]|&&|\|\|)/g, "\\$1");
    },

    valueToSolr(field, val) {
      const v = (val || "").trim();
      if (!v) return "";

      // Wenn User schon Solr-Syntax nutzt: respektieren
      if (/[":()]/.test(v) || /\b(AND|OR|NOT)\b/i.test(v) || /[*?]/.test(v)) {
        return v;
      }

      // Für text_general ohne Stemming: bei bestimmten Feldern Prefix-Match
      const prefixFields = new Set(["aufsatz:", "j_title:", "text:", "volume:", "heft:"]);
      const escaped = this.escapeSolrValue(v);

      if (prefixFields.has(field)) {
        // Leerzeichen -> phrase prefix ist komplizierter; hier: wenn Spaces, quote + * weglassen
        // (alternativ könntest du hier splitten und jedes Token mit * versehen)
        if (/\s/.test(v)) return `"${escaped}"`;
        return `${escaped}*`;
      }

      // Standard
      if (/\s/.test(v)) return `"${escaped}"`;
      return escaped;
    },


    submitSearch() {
      const validRows = this.rows.filter((r) => r.submitted_input?.trim());
      if (validRows.length === 0) {
        alert("Please enter at least one search term.");
        return;
      }

      const queryParts = validRows.map((row, index) => {
        const prefix = index === 0 ? "" : (row.selected_operator || " AND ");

        if (row.selected_field === "dateIssued:") {
          const clause = this.buildDateIssuedClause(row.submitted_input);
          if (!clause) {
            alert("Date Issued: please use YYYY, YYYY-MM, YYYY-MM-DD (or DD.MM.YYYY / MM.YYYY).");
            throw new Error("Invalid dateIssued input");
          }
          return `${prefix}${clause}`; // clause enthält dateIssued:[... TO ...]
        }

        const value = this.valueToSolr(row.selected_field, row.submitted_input);
        return `${prefix}${row.selected_field}${value}`;
      });

      const params = new URLSearchParams(window.location.search);
      params.set("q", queryParts.join(" "));
      params.set("page", "1");

      window.location.href = `${this.searchUrl}?${params.toString()}`;
    },

    pad2(x) {
      return String(x).padStart(2, "0");
    },

    buildDateIssuedClause(input) {
      const v = (input || "").trim();
      if (!v) return null;

      let year, month, day;

      // DD.MM.YYYY
      let m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (m) {
        day = m[1];
        month = m[2];
        year = m[3];
      }

      // MM.YYYY
      m = !year && v.match(/^(\d{1,2})\.(\d{4})$/);
      if (m) {
        month = m[1];
        year = m[2];
      }

      // YYYY-MM-DD
      m = !year && v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (m) {
        year = m[1];
        month = m[2];
        day = m[3];
      }

      // YYYY-MM
      m = !year && v.match(/^(\d{4})-(\d{1,2})$/);
      if (m) {
        year = m[1];
        month = m[2];
      }

      // YYYY
      m = !year && v.match(/^(\d{4})$/);
      if (m) {
        year = m[1];
      }

      // nur Monat/Tag ohne Jahr: in pdates nicht sinnvoll als Range ausdrückbar
      if (!year && (month || day)) return null;

      if (year && month && day) {
        const y = year, mo = this.pad2(month), d = this.pad2(day);
        return `dateIssued:[${y}-${mo}-${d}T00:00:00Z TO ${y}-${mo}-${d}T23:59:59Z]`;
      }

      if (year && month) {
        const y = year, mo = this.pad2(month);
        const lastDay = new Date(Date.UTC(Number(y), Number(mo), 0)).getUTCDate();
        return `dateIssued:[${y}-${mo}-01T00:00:00Z TO ${y}-${mo}-${this.pad2(lastDay)}T23:59:59Z]`;
      }

      if (year) {
        return `dateIssued:[${year}-01-01T00:00:00Z TO ${year}-12-31T23:59:59Z]`;
      }

      return null;
    },

    buildDateIssuedQuery(input) {
      const v = (input || "").trim();
      if (!v) return "";

      // akzeptiere: YYYY | YYYY-MM | YYYY-MM-DD
      // sowie: DD.MM.YYYY | MM.YYYY
      let year, month, day;

      // DD.MM.YYYY
      let m1 = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (m1) {
        day = m1[1];
        month = m1[2];
        year = m1[3];
      }

      // MM.YYYY
      let m2 = v.match(/^(\d{1,2})\.(\d{4})$/);
      if (!m1 && m2) {
        month = m2[1];
        year = m2[2];
      }

      // YYYY-MM-DD
      let m3 = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (!m1 && m3) {
        year = m3[1];
        month = m3[2];
        day = m3[3];
      }

      // YYYY-MM
      let m4 = v.match(/^(\d{4})-(\d{1,2})$/);
      if (!m1 && !m3 && m4) {
        year = m4[1];
        month = m4[2];
      }

      // YYYY
      let m5 = v.match(/^(\d{4})$/);
      if (!m1 && !m2 && !m3 && !m4 && m5) {
        year = m5[1];
      }

      // Nur Monat/Tag ohne Jahr -> nicht zuverlässig (siehe Erklärung)
      if (!year && (month || day)) {
        // UX: lieber klar sein, statt “komisch” zu suchen
        return null; // signalisiert: ungültig / bitte Jahr angeben
      }

      const pad2 = (x) => String(x).padStart(2, "0");

      if (year && month && day) {
        const y = year, mo = pad2(month), d = pad2(day);
        return `dateIssued:[${y}-${mo}-${d}T00:00:00Z TO ${y}-${mo}-${d}T23:59:59Z]`;
      }

      if (year && month) {
        const y = year, mo = pad2(month);
        // Monatsende korrekt berechnen (ohne hart 31)
        const lastDay = new Date(Date.UTC(Number(y), Number(mo), 0)).getUTCDate();
        return `dateIssued:[${y}-${mo}-01T00:00:00Z TO ${y}-${mo}-${pad2(lastDay)}T23:59:59Z]`;
      }

      if (year) {
        return `dateIssued:[${year}-01-01T00:00:00Z TO ${year}-12-31T23:59:59Z]`;
      }

      return null;
    },

    // ---- Parse q back into rows ----
    parseQueryToRows(qstring) {
      // split keeps operators
      const parts = qstring.split(/( AND | OR | NOT )/);
      const rows = [];

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (!part) continue;
        if (["AND", "OR", "NOT"].includes(part)) continue;

        // field:value (field can include underscore)
        const match = part.match(/^([A-Za-z_]\w*):(.+)$/);
        if (!match) continue;

        const field = match[1] + ":";
        const value = match[2].trim();
        const operator = i > 0 ? parts[i - 1].trim() : "";

        rows.push({
          selected_operator: operator ? ` ${operator} ` : "",
          selected_field: field,
          submitted_input: value,
        });
      }

      return rows;
    },
  },
};
</script>
